import { useEffect, useContext, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { CartContext } from '../contexts/CartContext';
import { formatPrice } from '../utils/apiHelpers';
import { trackViewCart } from '../utils/analytics';

function ShopCart() {
  const navigate = useNavigate();
  const {
    cart,
    loading,
    isEmpty,
    subtotal,
    total,
    updateQuantity,
    removeFromCart
  } = useContext(CartContext);

  // Handle quantity change
  const handleQuantityChange = async (itemId, newQuantity) => {
    const qty = parseInt(newQuantity);
    if (qty >= 1 && qty <= 10) {
      const result = await updateQuantity(itemId, qty);
      if (!result.success) {
        toast.error(result.error || 'Failed to update quantity');
      }
    }
  };

  // Handle remove item
  const handleRemove = async (itemId) => {
    const result = await removeFromCart(itemId);
    if (result.success) {
      toast.success('Item removed from cart');
    } else {
      toast.error(result.error || 'Failed to remove item');
    }
  };

  // Handle checkout navigation
  const handleCheckout = () => {
    navigate('/checkout');
  };

  // Track cart view when cart is loaded
  useEffect(() => {
    if (!loading && cart?.items && cart.items.length > 0) {
      trackViewCart(cart.items, subtotal);
    }
  }, [loading, cart, subtotal]);

  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Initialize WOW animations
    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[ShopCart] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner - Using banner4.jpg for shop cart page */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START HEADING SECTION */}
      <div className="about_content">

        <div className="container">
          <div className="row">
            <div className="col-12  text-center text-lg-left wow slideInUp" data-wow-duration="2s">
              <h1 className="heading">Get Ready For Checkout?</h1>
              <p className="para_text w-100 w-lg-75">Review your items below and proceed to our secure checkout when you're ready. We accept all major credit cards and offer fast, reliable shipping on all orders.</p>
            </div>
          </div>
        </div>


        {/* START SHOP CART SECTION */}
        <div className="shop-cart wow slideInUp" data-wow-duration="2s">
          <div className="container">
            {/* Empty Cart State */}
            {isEmpty && (
              <div className="row pt-5">
                <div className="col-12 text-center py-5">
                  <h3>Your cart is empty</h3>
                  <p className="mb-4">Looks like you haven't added any items to your cart yet.</p>
                  <Link to="/" className="btn pink-gradient-btn-into-black">
                    Continue Shopping
                  </Link>
                </div>
              </div>
            )}

            {/* START SHOP CART TABLE */}
            {!isEmpty && (
              <div className="row pt-5">
                <div className="col-12 col-md-12  cart_table wow fadeInUp animated" data-wow-delay="300ms" style={{visibility: 'visible', animationDelay: '300ms', animationName: 'fadeInUp'}}>
                  <div className="table-responsive">
                    <table className="table table-bordered border-radius">
                      <thead>
                        <tr>
                          <th className="darkcolor">Product</th>
                          <th className="darkcolor">Price</th>
                          <th className="darkcolor">Quantity</th>
                          <th className="darkcolor">Total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart?.items?.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-table product-detail-cart">
                                <div className="media">
                                  <div className="row no-gutters">
                                    <div className="col-12 col-lg-2 product-detail-cart-image">
                                      <Link className="shopping-product" to={`/product-detail/${item.productId}`}>
                                        <img
                                          src={item.product?.images?.[0]?.url || '/assets/img/shop-cart-thumb1.jpg'}
                                          alt={item.product?.name || 'Product'}
                                        />
                                      </Link>
                                    </div>
                                    <div className="col-12 col-lg-10 mt-auto product-detail-cart-data">
                                      <div className="media-body ml-lg-3">
                                        <h4 className="product-name">
                                          <Link to={`/product-detail/${item.productId}`}>
                                            {item.product?.name || 'Product'}
                                          </Link>
                                        </h4>
                                        <p className="product-des">{item.product?.description || 'Amazing art piece'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <h4 className="text-center amount">{formatPrice(item.price)}</h4>
                            </td>
                            <td className="text-center">
                              <div className="quote text-center mt-1">
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                  className="quote"
                                  min="1"
                                  max="10"
                                  disabled={loading}
                                />
                              </div>
                            </td>
                            <td>
                              <h4 className="text-center amount">{formatPrice(item.price * item.quantity)}</h4>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn-close"
                                onClick={() => handleRemove(item.id)}
                                disabled={loading}
                                aria-label="Remove item"
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                              >
                                <i className="lni-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="apply_coupon">
                    <div className="row">
                      <div className="col-12 text-left">
                        <Link to="/" className="btn pink-gradient-btn-into-black me-3">Continue Shopping</Link>
                        <button
                          onClick={handleCheckout}
                          className="btn pink-color-black-gradient-btn"
                          disabled={loading || isEmpty}
                        >
                          CHECKOUT
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* END SHOP CART TABLE */}

            {/* START CART TOTAL SECTION */}
            {!isEmpty && (
              <div className="row pt-5">
                <div className="col-12 col-lg-6 offset-lg-3 wow slideInUp text-center text-lg-left" data-wow-duration="2s">
                  <div className="card-total">
                    <h4 className="heading">Cart Total</h4>
                    <table>
                      <tbody>
                        <tr>
                          <td>Subtotal</td>
                          <td>{formatPrice(cart?.summary?.subtotal || subtotal)}</td>
                        </tr>
                        <tr>
                          <td>Shipping</td>
                          <td>
                            {cart?.summary?.hasFreeShipping ? (
                              <span className="text-success">FREE</span>
                            ) : (
                              <span>{formatPrice(cart?.summary?.shipping || 0)}</span>
                            )}
                            {!cart?.summary?.hasFreeShipping && cart?.summary?.freeShippingThreshold && (
                              <small className="d-block text-muted mt-1">
                                Add {formatPrice(cart.summary.freeShippingThreshold - (cart?.summary?.subtotal || 0))} more for free shipping!
                              </small>
                            )}
                          </td>
                        </tr>
                        {cart?.summary?.tax > 0 && (
                          <tr>
                            <td>Tax (8%)</td>
                            <td>{formatPrice(cart.summary.tax)}</td>
                          </tr>
                        )}
                        {cart?.summary?.discount > 0 && (
                          <tr>
                            <td>Discount</td>
                            <td className="text-success">-{formatPrice(cart.summary.discount)}</td>
                          </tr>
                        )}
                        <tr className="total-row" style={{ fontWeight: 'bold', borderTop: '2px solid #ddd' }}>
                          <td>Total</td>
                          <td>{formatPrice(cart?.summary?.total || total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* END CART TOTAL SECTION */}

          </div>
        </div>
        {/* END SHOP CART SECTION*/}

      </div>
      {/* END HEADING SECTION */}
    </Layout>
  );
}

export default ShopCart;
